import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Typography,
  Chip,
  Paper,
  RadioGroup,
  Radio,
  FormLabel,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
} from '@mui/icons-material';
import { employeeAPI, departmentAPI, holidayGroupAPI, employeeGroupAPI, systemConfigAPI, bankAPI, employeeBankAccountAPI } from '../../services/api';

// FastAPI validation errors (422) return `detail` as an array of
// {type, loc, msg, ...} objects rather than a string - render that raw would
// crash React ("Objects are not valid as a React child"), so flatten it.
function formatApiError(err, fallback) {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  }
  return fallback;
}

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [holidayGroups, setHolidayGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [banks, setBanks] = useState([]);
  const [leaveSummaryMap, setLeaveSummaryMap] = useState({});
  const [defaultMaxCarryForward, setDefaultMaxCarryForward] = useState(8);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    emp_name: '',
    emp_dept: '',
    number_of_punch: 2,
    week_off_day: '',  // Changed to empty string for "None" option
    flexi_week_off: false,
    overtime_allowed: false,
    total_working_hours: 0,
    overtime_threshold_minutes: 0,
    ot_rate_type: 'Fixed',
    ot_rate: 0,
    joining_date: '2025-01-01',
    active: true,
    disabled_date: '',
    pay_applicable: true,
    mobile_country_code: '91',
    mobile_number: '',  // Optional
    emergency_country_code: '91',
    emergency_contact_number: '',  // Optional
    address: '',
    review_required: false,
    paid_lunch_break: false,
    holiday_group_ids: [],
    emp_group_id: null,
    comment: '',
    leaves_applicable: true,
    leaves_per_annum: '',
    max_carry_forward: '',
    carried_forward_leaves: '',
    leaves_availed_opening: 0,
    leaves_effective_date: '',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Bank accounts sub-state (edit mode only)
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccDialogOpen, setBankAccDialogOpen] = useState(false);
  const [editingBankAcc, setEditingBankAcc] = useState(null);
  const [bankAccForm, setBankAccForm] = useState({
    bank_id: '', account_holder_name: '', account_number: '',
    ifsc_code: '', account_type: 'Primary', pay_to_account: false,
  });
  const [bankAccError, setBankAccError] = useState('');

  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const [punchOptions, setPunchOptions] = useState([1, 2, 3, 4, 5, 6]);

  useEffect(() => {
    loadEmployees();
    loadDepartments();
    loadHolidayGroups();
    loadGroups();
    loadBanks();
    loadLeaveSummary();
    loadDefaultCarryForward();
    loadPunchOptions();
  }, []);

  // Load / clear bank accounts whenever the editing target changes
  useEffect(() => {
    if (editing) {
      loadBankAccounts(editing.emp_id);
    } else {
      setBankAccounts([]);
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.data);
    } catch (error) {
      setError('Failed to load employees');
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await departmentAPI.getAll();
      setDepartments(response.data);
    } catch (error) {
      setError('Failed to load departments');
    }
  };

  const loadHolidayGroups = async () => {
    try {
      const response = await holidayGroupAPI.getAll();
      setHolidayGroups(response.data);
    } catch (error) {
      console.error('Failed to load holiday groups');
    }
  };

  const loadGroups = async () => {
    try {
      const response = await employeeGroupAPI.getAll();
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to load groups');
    }
  };

  const loadBanks = async () => {
    try {
      const response = await bankAPI.getAll();
      setBanks(response.data);
    } catch (error) {
      console.error('Failed to load banks');
    }
  };

  const loadLeaveSummary = async () => {
    try {
      const response = await employeeAPI.getLeaveSummary();
      const map = {};
      (response.data || []).forEach(item => { map[item.emp_id] = item; });
      setLeaveSummaryMap(map);
    } catch (error) {
      console.error('Failed to load leave summary');
    }
  };

  const loadDefaultCarryForward = async () => {
    try {
      const response = await systemConfigAPI.getAll();
      const cfg = (response.data || []).find(c => c.config_key === 'carry_forward_leaves');
      if (cfg) setDefaultMaxCarryForward(Number(cfg.config_value));
    } catch (error) {
      console.error('Failed to load system config');
    }
  };

  const loadPunchOptions = async () => {
    try {
      const response = await systemConfigAPI.getAll();
      const cfg = (response.data || []).find(c => c.config_key === 'available_punches');
      if (cfg) {
        const parsed = cfg.config_value
          .split(',')
          .map(v => parseInt(v.trim(), 10))
          .filter(v => !isNaN(v))
          .sort((a, b) => a - b);
        if (parsed.length > 0) setPunchOptions(parsed);
      }
    } catch (error) {
      console.error('Failed to load available punch options');
    }
  };

  const loadBankAccounts = async (empId) => {
    try {
      const response = await employeeBankAccountAPI.getAll(empId);
      setBankAccounts(response.data);
    } catch (err) {
      console.error('Failed to load bank accounts');
    }
  };

  const resetBankAccForm = () => {
    setBankAccForm({
      bank_id: '', account_holder_name: '', account_number: '',
      ifsc_code: '', account_type: 'Primary', pay_to_account: false,
    });
    setEditingBankAcc(null);
    setBankAccError('');
  };

  const handleOpenBankAccDialog = (acc = null) => {
    if (acc) {
      setEditingBankAcc(acc);
      setBankAccForm({
        bank_id: acc.bank_id ?? '',
        account_holder_name: acc.account_holder_name || '',
        account_number: acc.account_number,
        ifsc_code: acc.ifsc_code,
        account_type: acc.account_type,
        pay_to_account: acc.pay_to_account,
      });
    } else {
      resetBankAccForm();
    }
    setBankAccDialogOpen(true);
  };

  const handleSaveBankAccount = async () => {
    if (!bankAccForm.account_number || !bankAccForm.ifsc_code) {
      setBankAccError('Account Number and IFSC Code are required');
      return;
    }
    const payload = {
      ...bankAccForm,
      bank_id: bankAccForm.bank_id || null,
      account_holder_name: bankAccForm.account_holder_name || null,
    };
    try {
      if (editingBankAcc) {
        await employeeBankAccountAPI.update(editing.emp_id, editingBankAcc.id, payload);
      } else {
        await employeeBankAccountAPI.create(editing.emp_id, payload);
      }
      setBankAccDialogOpen(false);
      resetBankAccForm();
      loadBankAccounts(editing.emp_id);
    } catch (err) {
      setBankAccError(formatApiError(err, 'Failed to save bank account'));
    }
  };

  const handleDeleteBankAccount = async (id) => {
    if (!window.confirm('Delete this bank account?')) return;
    try {
      await employeeBankAccountAPI.delete(editing.emp_id, id);
      loadBankAccounts(editing.emp_id);
    } catch (err) {
      setError(formatApiError(err, 'Failed to delete bank account'));
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.emp_name || !formData.emp_dept || !formData.joining_date) {
        setError('Please fill all required fields');
        return;
      }

      if (!formData.active && !formData.disabled_date) {
        setError('Disabled Date is required to disable this employee');
        return;
      }

      // Convert empty-string form values to null - these are Optional[int]/
      // Optional[float] on the backend, and an empty string fails Pydantic
      // validation there (422) rather than being treated as "not provided".
      const dataToSubmit = {
        ...formData,
        week_off_day: formData.week_off_day || null,
        mobile_number: formData.mobile_number || null,
        emergency_contact_number: formData.emergency_contact_number || null,
        leaves_per_annum: formData.leaves_per_annum === '' ? null : formData.leaves_per_annum,
        max_carry_forward: formData.max_carry_forward === '' ? null : formData.max_carry_forward,
        carried_forward_leaves: formData.carried_forward_leaves === '' ? null : formData.carried_forward_leaves,
        leaves_effective_date: formData.leaves_effective_date || null,
        disabled_date: formData.active ? null : (formData.disabled_date || null),
      };

      if (editing) {
        await employeeAPI.update(editing.emp_id, dataToSubmit);
        setMessage('Employee updated successfully');
      } else {
        await employeeAPI.create(dataToSubmit);
        setMessage('Employee created successfully');
      }
      setOpen(false);
      loadEmployees();
      resetForm();
      setError('');
    } catch (error) {
      setError(formatApiError(error, 'Operation failed'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee? (Soft delete)')) {
      try {
        await employeeAPI.delete(id);
        setMessage('Employee deleted successfully');
        loadEmployees();
      } catch (error) {
        setError(formatApiError(error, 'Failed to delete employee'));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      emp_name: '',
      emp_dept: '',
      number_of_punch: 2,
      week_off_day: '',  // None by default
      flexi_week_off: false,
      overtime_allowed: false,
      total_working_hours: 0,
      overtime_threshold_minutes: 0,
      ot_rate_type: 'Fixed',
      ot_rate: 0,
      joining_date: '2025-01-01',
      active: true,
      disabled_date: '',
      pay_applicable: true,
      mobile_country_code: '91',
      mobile_number: '',
      emergency_country_code: '91',
      emergency_contact_number: '',
      address: '',
      review_required: false,
      paid_lunch_break: false,
      holiday_group_ids: [],
      emp_group_id: null,
      comment: '',
      leaves_applicable: true,
      leaves_per_annum: '',
      max_carry_forward: defaultMaxCarryForward,
      carried_forward_leaves: '',
      leaves_availed_opening: 0,
      leaves_effective_date: '',
    });
    setEditing(null);
    setError('');
  };

  const handleEdit = (employee) => {
    setEditing(employee);
    setFormData({
      emp_name: employee.emp_name,
      emp_dept: employee.emp_dept,
      number_of_punch: employee.number_of_punch,
      week_off_day: employee.week_off_day || '',  // Handle null
      flexi_week_off: employee.flexi_week_off || false,
      overtime_allowed: employee.overtime_allowed,
      total_working_hours: employee.total_working_hours ?? 0,
      overtime_threshold_minutes: employee.overtime_threshold_minutes ?? 0,
      ot_rate_type: employee.ot_rate_type || 'Fixed',
      ot_rate: employee.ot_rate ?? 0,
      joining_date: employee.joining_date,
      active: employee.active,
      disabled_date: employee.disabled_date || '',
      pay_applicable: employee.pay_applicable ?? true,
      mobile_country_code: employee.mobile_country_code,
      mobile_number: employee.mobile_number || '',  // Handle null
      emergency_country_code: employee.emergency_country_code,
      emergency_contact_number: employee.emergency_contact_number || '',  // Handle null
      address: employee.address || '',
      review_required: employee.review_required,
      paid_lunch_break: employee.paid_lunch_break || false,
      holiday_group_ids: employee.holiday_group_ids || [],
      emp_group_id: employee.emp_group_id || null,
      comment: employee.comment || '',
      leaves_applicable: employee.leaves_applicable ?? true,
      leaves_per_annum: employee.leaves_per_annum ?? '',
      max_carry_forward: employee.max_carry_forward ?? defaultMaxCarryForward,
      carried_forward_leaves: employee.carried_forward_leaves ?? '',
      leaves_availed_opening: employee.leaves_availed_opening ?? 0,
      leaves_effective_date: employee.leaves_effective_date || '',
    });
    setOpen(true);
  };

  const handleSelectEmployee = (employee) => {
    // Toggle selection
    if (selectedEmployee?.emp_id === employee.emp_id) {
      setSelectedEmployee(null);
    } else {
      setSelectedEmployee(employee);
    }
  };

  const handleCopyClick = () => {
    if (!selectedEmployee) return;
    
    // Pre-fill form with selected employee data (except emp_id)
    setFormData({
      emp_name: selectedEmployee.emp_name,
      emp_dept: selectedEmployee.emp_dept,
      number_of_punch: selectedEmployee.number_of_punch,
      week_off_day: selectedEmployee.week_off_day || '',  // Handle null
      flexi_week_off: selectedEmployee.flexi_week_off || false,
      overtime_allowed: selectedEmployee.overtime_allowed,
      total_working_hours: selectedEmployee.total_working_hours ?? 0,
      overtime_threshold_minutes: selectedEmployee.overtime_threshold_minutes ?? 0,
      ot_rate_type: selectedEmployee.ot_rate_type || 'Fixed',
      ot_rate: selectedEmployee.ot_rate ?? 0,
      joining_date: selectedEmployee.joining_date,
      active: selectedEmployee.active,
      disabled_date: selectedEmployee.disabled_date || '',
      pay_applicable: selectedEmployee.pay_applicable ?? true,
      mobile_country_code: selectedEmployee.mobile_country_code,
      mobile_number: selectedEmployee.mobile_number || '',  // Handle null
      emergency_country_code: selectedEmployee.emergency_country_code,
      emergency_contact_number: selectedEmployee.emergency_contact_number || '',  // Handle null
      address: selectedEmployee.address || '',
      review_required: selectedEmployee.review_required,
      paid_lunch_break: selectedEmployee.paid_lunch_break || false,
      holiday_group_ids: selectedEmployee.holiday_group_ids || [],
      emp_group_id: selectedEmployee.emp_group_id || null,
      comment: selectedEmployee.comment || '',
      leaves_applicable: selectedEmployee.leaves_applicable ?? true,
      leaves_per_annum: selectedEmployee.leaves_per_annum ?? '',
      max_carry_forward: selectedEmployee.max_carry_forward ?? defaultMaxCarryForward,
      carried_forward_leaves: selectedEmployee.carried_forward_leaves ?? '',
      leaves_availed_opening: selectedEmployee.leaves_availed_opening ?? 0,
      leaves_effective_date: selectedEmployee.leaves_effective_date || '',
    });

    // Open create dialog (not edit)
    setEditing(null);
    setOpen(true);
    
    // Clear selection
    setSelectedEmployee(null);
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.dept_id === deptId);
    return dept ? dept.dept_name : 'Unknown';
  };

  const getGroupName = (groupId) => {
    if (!groupId) return '-';
    const group = groups.find(g => g.group_id === groupId);
    return group ? group.group_name : 'Unknown';
  };

  // SBI doesn't use IFSC codes the way other banks do - the field gets
  // locked with an explanatory value instead of a real code.
  const isSbiBankSelected = banks.find((b) => b.bank_id === bankAccForm.bank_id)?.bank_name?.trim().toUpperCase() === 'SBI BANK';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Typography variant="h6">Employee Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            Add Employee
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<ContentCopy />}
            onClick={handleCopyClick}
            disabled={!selectedEmployee}
          >
            Copy Selected
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">Select</TableCell>
              <TableCell>Employee ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Week Off</TableCell>
              <TableCell>Pay Group</TableCell>
              <TableCell>Joining Date</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...employees]
              .sort((a, b) => {
                if (b.active !== a.active) return b.active ? 1 : -1;
                return new Date(b.created_at) - new Date(a.created_at);
              })
              .map((employee) => (
              <TableRow 
                key={employee.emp_id}
                hover
                selected={selectedEmployee?.emp_id === employee.emp_id}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedEmployee?.emp_id === employee.emp_id}
                    onChange={() => handleSelectEmployee(employee)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>{employee.emp_id}</TableCell>
                <TableCell>{employee.emp_name}</TableCell>
                <TableCell>{getDepartmentName(employee.emp_dept)}</TableCell>
                <TableCell>{employee.week_off_day || 'None'}</TableCell>
                <TableCell>{getGroupName(employee.emp_group_id)}</TableCell>
                <TableCell>{new Date(employee.joining_date).toLocaleDateString()}</TableCell>
                <TableCell>{new Date(employee.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <Chip
                    label={employee.active ? 'Active' : 'Inactive'} 
                    color={employee.active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEdit(employee)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(employee.emp_id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Employee Name"
                value={formData.emp_name}
                onChange={(e) => setFormData({ ...formData, emp_name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.emp_dept}
                  label="Department"
                  onChange={(e) => setFormData({ ...formData, emp_dept: e.target.value })}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.dept_id} value={dept.dept_id}>
                      {dept.dept_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Number of Punches</InputLabel>
                <Select
                  value={formData.number_of_punch}
                  label="Number of Punches"
                  onChange={(e) => setFormData({ ...formData, number_of_punch: e.target.value })}
                >
                  {punchOptions.map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={formData.number_of_punch === 0}>
                <InputLabel>Week Off Day</InputLabel>
                <Select
                  value={formData.week_off_day}
                  label="Week Off Day"
                  onChange={(e) => setFormData({ ...formData, week_off_day: e.target.value })}
                >
                  <MenuItem value="">None (No Week Off)</MenuItem>
                  {weekDays.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="Alternate Sunday">Alternate Sunday (1st &amp; 3rd only)</MenuItem>
                </Select>
                {formData.number_of_punch === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Not applicable - 0-punch employees are always marked Present
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.flexi_week_off}
                    onChange={(e) => setFormData({ ...formData, flexi_week_off: e.target.checked })}
                    disabled={!formData.week_off_day}
                  />
                }
                label={
                  <Typography variant="body2">
                    Flexi Week Off
                    {formData.flexi_week_off && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        First {formData.week_off_day ? '' : '?'} absents count as WO
                      </Typography>
                    )}
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={formData.number_of_punch === 0}>
                <InputLabel>Holiday Group(s)</InputLabel>
                <Select
                  multiple
                  value={formData.holiday_group_ids}
                  label="Holiday Group(s)"
                  onChange={(e) => {
                    const { value } = e.target;
                    setFormData({
                      ...formData,
                      // MUI multi-select delivers a comma-separated string on
                      // some browsers/events instead of an array - normalize.
                      holiday_group_ids: typeof value === 'string' ? value.split(',').map(Number) : value,
                    });
                  }}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? 'None (No Holidays)'
                      : selected
                          .map((id) => holidayGroups.find((g) => g.group_id === id)?.group_name)
                          .filter(Boolean)
                          .join(', ')
                  }
                >
                  {holidayGroups.map((group) => (
                    <MenuItem key={group.group_id} value={group.group_id}>
                      <Checkbox checked={formData.holiday_group_ids.includes(group.group_id)} />
                      {group.group_name}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formData.number_of_punch === 0
                    ? 'Not applicable - 0-punch employees are always marked Present'
                    : 'A date is a holiday for this employee if it\'s in ANY selected group'}
                </Typography>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Pay Group</InputLabel>
                <Select
                  value={formData.emp_group_id || ''}
                  label="Pay Group"
                  onChange={(e) => setFormData({ ...formData, emp_group_id: e.target.value || null })}
                >
                  <MenuItem value="">None</MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.group_id} value={group.group_id}>
                      {group.group_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Joining Date"
                value={formData.joining_date}
                onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(e) => {
                      const isActive = e.target.checked;
                      setFormData({
                        ...formData,
                        active: isActive,
                        // No default value when switching off - left blank
                        // so Save is blocked until a real date is entered.
                        // Cleared when switching back on, since a re-enabled
                        // employee has no disabled date.
                        disabled_date: isActive ? '' : formData.disabled_date,
                      });
                    }}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.pay_applicable}
                    onChange={(e) => setFormData({ ...formData, pay_applicable: e.target.checked })}
                  />
                }
                label="Prepare Payroll Statement"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.paid_lunch_break}
                    onChange={(e) => setFormData({ ...formData, paid_lunch_break: e.target.checked })}
                  />
                }
                label="Paid Lunch Break"
              />
            </Grid>

            {!formData.active && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Disabled Date"
                  value={formData.disabled_date}
                  onChange={(e) => setFormData({ ...formData, disabled_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="The date this employee actually stopped working - required to disable"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Contact Information (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Country Code"
                value={formData.mobile_country_code}
                onChange={(e) => setFormData({ ...formData, mobile_country_code: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={9}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={formData.mobile_number}
                onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                helperText="Optional - 10 digits"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Emergency Country Code"
                value={formData.emergency_country_code}
                onChange={(e) => setFormData({ ...formData, emergency_country_code: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={9}>
              <TextField
                fullWidth
                label="Emergency Contact Number"
                value={formData.emergency_contact_number}
                onChange={(e) => setFormData({ ...formData, emergency_contact_number: e.target.value })}
                helperText="Optional - 10 digits"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>

            {/* ── Leave Tracking ─────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Leave Tracking
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.leaves_applicable}
                    onChange={(e) => setFormData({ ...formData, leaves_applicable: e.target.checked })}
                  />
                }
                label="Leaves Applicable"
              />
            </Grid>

            {formData.leaves_applicable && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="# Leaves p.a."
                    value={formData.leaves_per_annum}
                    onChange={(e) => setFormData({ ...formData, leaves_per_annum: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Carry Forward"
                    value={formData.max_carry_forward}
                    onChange={(e) => setFormData({ ...formData, max_carry_forward: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    inputProps={{ min: 0 }}
                    helperText={`System default: ${defaultMaxCarryForward}`}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Carried from Last Year"
                    value={formData.carried_forward_leaves}
                    onChange={(e) => setFormData({ ...formData, carried_forward_leaves: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                    inputProps={{ min: 0, step: 0.5 }}
                    helperText="Auto-set on Jan upload; editable"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Leave Availed (Opening Balance)"
                    value={formData.leaves_availed_opening}
                    onChange={(e) => setFormData({ ...formData, leaves_availed_opening: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    inputProps={{ min: 0, step: 0.5 }}
                    helperText="Leaves taken before system setup"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Leaves Effective Date"
                    value={formData.leaves_effective_date}
                    onChange={(e) => setFormData({ ...formData, leaves_effective_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Blank = applies as today (from Joining Date). If set, Leave Earned/Eligible Remaining start from that month - 0 before it."
                  />
                </Grid>

                {/* Computed read-only fields — shown only when editing an existing employee */}
                {editing && (() => {
                  const ls = leaveSummaryMap[editing.emp_id] || {};
                  const lpa = formData.leaves_per_annum || 0;
                  const cf  = parseFloat(formData.carried_forward_leaves) || 0;
                  const totalAvail = lpa + cf;
                  return (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff8f0' }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#e65100' }}>
                          Leave Summary (Current Year)
                        </Typography>
                        <Grid container spacing={2}>
                          {[
                            { label: 'Total Leaves Available', value: totalAvail },
                            { label: 'Leave Earned (to date)', value: ls.leave_earned ?? '—' },
                            { label: 'Leave Availed (total)', value: ls.leave_availed ?? '—' },
                            { label: 'Leave Remaining', value: ls.leave_remaining ?? '—' },
                          ].map(({ label, value }) => (
                            <Grid item xs={6} sm={3} key={label}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="body1" fontWeight="bold">{value}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  );
                })()}
              </>
            )}

            {/* ── Bank Accounts (edit mode only) ───────────────────────────── */}
            {editing && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="subtitle2">Bank Accounts</Typography>
                    <Button size="small" startIcon={<Add />} onClick={() => handleOpenBankAccDialog()}>
                      Add Account
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  {bankAccounts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      No bank accounts added yet.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Bank</TableCell>
                            <TableCell>Account Holder</TableCell>
                            <TableCell>Account No.</TableCell>
                            <TableCell>IFSC</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Pay To</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bankAccounts.map((acc) => (
                            <TableRow key={acc.id}>
                              <TableCell>{acc.bank_name || '—'}</TableCell>
                              <TableCell>{acc.account_holder_name || '—'}</TableCell>
                              <TableCell>{acc.account_number}</TableCell>
                              <TableCell>{acc.ifsc_code}</TableCell>
                              <TableCell>
                                <Chip
                                  label={acc.account_type}
                                  size="small"
                                  color={acc.account_type === 'Primary' ? 'primary' : 'default'}
                                />
                              </TableCell>
                              <TableCell>{acc.pay_to_account ? 'Yes' : '—'}</TableCell>
                              <TableCell>
                                <IconButton size="small" onClick={() => handleOpenBankAccDialog(acc)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteBankAccount(acc.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>
              </>
            )}

            {/* ── Overtime Configuration ──────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Overtime Configuration
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.overtime_allowed}
                    onChange={(e) => setFormData({ ...formData, overtime_allowed: e.target.checked })}
                  />
                }
                label="Overtime Allowed"
              />
            </Grid>

            {formData.overtime_allowed && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Total Working Hours (minutes)"
                  helperText="0 = use the Department's value"
                  value={formData.total_working_hours}
                  onChange={(e) => setFormData({ ...formData, total_working_hours: parseInt(e.target.value) || 0 })}
                />
              </Grid>
            )}

            {formData.overtime_allowed && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Overtime Threshold (minutes)"
                  helperText="0 = use the Department's value"
                  value={formData.overtime_threshold_minutes}
                  onChange={(e) => setFormData({ ...formData, overtime_threshold_minutes: parseInt(e.target.value) || 0 })}
                />
              </Grid>
            )}

            {formData.overtime_allowed && (
              <Grid item xs={12}>
                <FormControl component="fieldset" sx={{ mb: 1 }}>
                  <FormLabel component="legend" sx={{ fontSize: '0.8rem' }}>Hourly OT Rate</FormLabel>
                  <RadioGroup
                    value={formData.ot_rate_type}
                    onChange={(e) => setFormData({ ...formData, ot_rate_type: e.target.value })}
                  >
                    <FormControlLabel
                      value="Salary Based"
                      control={<Radio size="small" />}
                      label="Salary Based Hourly OT Rate (calculated from Gross Salary, no manual entry)"
                    />
                    <FormControlLabel
                      value="Fixed"
                      control={<Radio size="small" />}
                      label="Fixed Hourly OT Rate for Employee"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}

            {formData.overtime_allowed && formData.ot_rate_type === 'Fixed' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Fixed Hourly OT Rate (₹)"
                  value={formData.ot_rate}
                  onChange={(e) => setFormData({ ...formData, ot_rate: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            )}

            {formData.overtime_allowed && formData.ot_rate_type === 'Salary Based' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled
                  label="Salary Based Hourly OT Rate (₹)"
                  value={formData.ot_rate?.toFixed ? formData.ot_rate.toFixed(2) : formData.ot_rate}
                  helperText="Gross Salary ÷ (30 days * 8 Hrs) - recalculates automatically when salary changes in Configure Salary. Save this employee to refresh the value shown here."
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comment"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Any notes or remarks about this employee"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpen(false);
            resetForm();
          }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bank Account Add / Edit dialog ──────────────────────────────── */}
      <Dialog
        open={bankAccDialogOpen}
        onClose={() => { setBankAccDialogOpen(false); resetBankAccForm(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingBankAcc ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle>
        <DialogContent>
          {bankAccError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBankAccError('')}>
              {bankAccError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Bank Name</InputLabel>
                <Select
                  value={bankAccForm.bank_id}
                  label="Bank Name"
                  onChange={(e) => {
                    const bankId = e.target.value;
                    const selectedBank = banks.find((b) => b.bank_id === bankId);
                    const isSbi = selectedBank?.bank_name?.trim().toUpperCase() === 'SBI BANK';
                    setBankAccForm({
                      ...bankAccForm,
                      bank_id: bankId,
                      // SBI doesn't use IFSC codes the way other banks do -
                      // pre-fill and lock the field rather than leave it
                      // editable. Clear it back out if switching away from
                      // SBI, but only when it still holds that auto-fill
                      // value - don't clobber something the user typed.
                      ifsc_code: isSbi
                        ? 'Not Required'
                        : (bankAccForm.ifsc_code === 'Not Required' ? '' : bankAccForm.ifsc_code),
                    });
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank.bank_id} value={bank.bank_id}>
                      {bank.bank_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Holder Name"
                value={bankAccForm.account_holder_name}
                onChange={(e) => setBankAccForm({ ...bankAccForm, account_holder_name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Account Number"
                value={bankAccForm.account_number}
                onChange={(e) => setBankAccForm({ ...bankAccForm, account_number: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="IFSC Code"
                value={bankAccForm.ifsc_code}
                disabled={isSbiBankSelected}
                onChange={(e) => setBankAccForm({ ...bankAccForm, ifsc_code: e.target.value.toUpperCase() })}
                inputProps={{ style: isSbiBankSelected ? undefined : { textTransform: 'uppercase' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={bankAccForm.account_type}
                  label="Account Type"
                  onChange={(e) => setBankAccForm({ ...bankAccForm, account_type: e.target.value })}
                >
                  <MenuItem value="Primary">Primary</MenuItem>
                  <MenuItem value="Secondary">Secondary</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bankAccForm.pay_to_account}
                    onChange={(e) => setBankAccForm({ ...bankAccForm, pay_to_account: e.target.checked })}
                  />
                }
                label="Pay To Account"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBankAccDialogOpen(false); resetBankAccForm(); }}>Cancel</Button>
          <Button onClick={handleSaveBankAccount} variant="contained">
            {editingBankAcc ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeManagement;
